import React from 'react';
import { useSelector } from 'react-redux';
import classNames from 'classnames';

// Configs, contexts and utils
import { useConfiguration } from '../../context/configurationContext';
import { useIntl } from '../../util/reactIntl';
import { isScrollingDisabled } from '../../ducks/ui.duck';

// Shared components
import { LayoutSingleColumn, NamedLink, Page } from '../../components';

// Containers
import TopbarContainer from '../TopbarContainer/TopbarContainer';
import FooterContainer from '../FooterContainer/FooterContainer';
import { TRUSTPILOT_URL } from '../BecomeTrainerPage/becomeTrainerContent';

// Same directory
import {
  AUDIENCES,
  COMMITMENTS,
  FINAL_CTA,
  FOUNDERS,
  HERO,
  MISSION,
  REVIEWS,
  STATS,
  STORY,
  TRUSTPILOT_SCORE,
  VALUES,
} from './aboutContent';

import css from './AboutPage.module.css';

const Actions = ({ ghostClassName }) => (
  <div className={css.actions}>
    <NamedLink name="SearchPage" className={css.buttonPrimary}>
      Trouver une formation
    </NamedLink>
    <NamedLink name="BecomeTrainerPage" className={ghostClassName}>
      Devenir formateur
    </NamedLink>
  </div>
);

/**
 * "À propos" page: who is behind Formabel, its mission, who it is for, reviews and commitments.
 *
 * @component
 * @returns {JSX.Element}
 */
const AboutPage = () => {
  const intl = useIntl();
  const config = useConfiguration();
  const scrollingDisabled = useSelector(state => isScrollingDisabled(state));
  const rootURL = config.marketplaceRootURL;

  const schema = {
    '@context': 'http://schema.org',
    '@type': 'AboutPage',
    url: `${rootURL}/a-propos`,
    inLanguage: 'fr-BE',
    mainEntity: {
      '@type': 'Organization',
      name: config.marketplaceName,
      url: rootURL,
      areaServed: 'BE',
      founder: FOUNDERS.map(f => ({ '@type': 'Person', name: f.name, jobTitle: f.role })),
    },
  };

  return (
    <Page
      title={intl.formatMessage(
        { id: 'AboutPage.metaTitle' },
        { marketplaceName: config.marketplaceName }
      )}
      description={intl.formatMessage({ id: 'AboutPage.metaDescription' })}
      schema={schema}
      scrollingDisabled={scrollingDisabled}
    >
      <LayoutSingleColumn topbar={<TopbarContainer />} footer={<FooterContainer />}>
        <div className={css.root}>
          <header className={css.hero}>
            <div className={css.heroGrid}>
              <div>
                <p className={css.kicker}>{HERO.kicker}</p>
                <h1 className={css.title}>
                  {HERO.title} <span className={css.titleAccent}>{HERO.titleAccent}</span>
                </h1>
                <p className={css.lead}>{HERO.lead}</p>
                <Actions ghostClassName={css.buttonGhost} />
              </div>
              <div className={css.founders}>
                {FOUNDERS.map(f => (
                  <figure key={f.name} className={css.founder}>
                    <img
                      className={css.founderPhoto}
                      src={f.image}
                      alt={`${f.name}, ${f.role.toLowerCase()} de Formabel`}
                    />
                    <figcaption className={css.founderTag}>
                      <strong>{f.name}</strong>
                      <span>{f.role}</span>
                    </figcaption>
                  </figure>
                ))}
              </div>
            </div>
            <dl className={css.stats}>
              {STATS.map(s => (
                <div key={s.label} className={css.stat}>
                  <dt className={css.statValue}>{s.value}</dt>
                  <dd className={css.statLabel}>{s.label}</dd>
                </div>
              ))}
            </dl>
          </header>

          <div className={css.container}>
            <section className={css.mission}>
              <p className={css.missionLabel}>{MISSION.label}</p>
              <p className={css.missionText}>
                {MISSION.text} <em className={css.missionAccent}>{MISSION.accent}</em>
              </p>
            </section>

            <section className={css.section}>
              <h2 className={css.sectionTitle}>Comment tout a commencé</h2>
              <ol className={css.story}>
                {STORY.map(s => (
                  <li key={s.title} className={css.storyItem}>
                    <h3 className={css.storyTitle}>{s.title}</h3>
                    <p className={css.storyText}>{s.text}</p>
                  </li>
                ))}
              </ol>
            </section>

            <section className={css.section}>
              <h2 className={css.sectionTitle}>Pour qui ?</h2>
              <div className={css.audiences}>
                {AUDIENCES.map(a => (
                  <div
                    key={a.label}
                    className={classNames(css.audience, {
                      [css.audienceHighlighted]: a.highlighted,
                    })}
                  >
                    <p className={css.audienceLabel}>{a.label}</p>
                    <h3 className={css.cardTitle}>{a.title}</h3>
                    <p className={css.cardText}>{a.text}</p>
                    {a.tags ? (
                      <ul className={css.tags}>
                        {a.tags.map(t => (
                          <li key={t} className={css.tag}>
                            {t}
                          </li>
                        ))}
                      </ul>
                    ) : null}
                  </div>
                ))}
              </div>
            </section>

            <section className={css.section}>
              <h2 className={css.sectionTitle}>Ils nous font confiance</h2>
              <p className={css.score}>
                <span className={css.scoreStars} aria-hidden="true">
                  ★★★★★
                </span>
                <strong>{TRUSTPILOT_SCORE}</strong> sur Trustpilot
              </p>
              <div className={css.reviews}>
                {REVIEWS.map(r => (
                  <figure key={r.author} className={css.review}>
                    <p className={css.reviewStars}>
                      <span aria-hidden="true">★★★★★</span>
                      <span className={css.verified}>Vérifié</span>
                    </p>
                    <blockquote className={css.reviewText}>« {r.text} »</blockquote>
                    <figcaption className={css.reviewAuthor}>
                      <strong>{r.author}</strong>
                      <span>{r.role}</span>
                    </figcaption>
                  </figure>
                ))}
              </div>
              <a
                className={css.textLink}
                href={TRUSTPILOT_URL}
                target="_blank"
                rel="noopener noreferrer"
              >
                Lire tous les avis sur Trustpilot →
              </a>
            </section>

            <section className={css.section}>
              <h2 className={css.sectionTitle}>Nos engagements</h2>
              <ol className={css.commitments}>
                {COMMITMENTS.map((c, i) => (
                  <li key={c.title} className={css.commitment}>
                    <span className={css.commitmentNumber} aria-hidden="true">
                      {i + 1}
                    </span>
                    <div>
                      <h3 className={css.cardTitle}>{c.title}</h3>
                      <p className={css.cardText}>{c.text}</p>
                    </div>
                  </li>
                ))}
              </ol>
            </section>

            <section className={css.section}>
              <h2 className={css.sectionTitle}>Ce qui nous guide</h2>
              <ol className={css.values}>
                {VALUES.map((v, i) => (
                  <li key={v.title} className={css.value}>
                    <span className={css.valueNumber}>{`0${i + 1}`}</span>
                    <h3 className={css.cardTitle}>{v.title}</h3>
                    <p className={css.cardText}>{v.text}</p>
                  </li>
                ))}
              </ol>
            </section>

            <section className={css.final}>
              <h2 className={css.finalTitle}>{FINAL_CTA.title}</h2>
              <p className={css.finalText}>{FINAL_CTA.text}</p>
              <Actions ghostClassName={css.buttonGhostLight} />
            </section>
          </div>
        </div>
      </LayoutSingleColumn>
    </Page>
  );
};

export default AboutPage;
